/* COOLCHAP global.js — vanilla, no dependencies */
(function () {
  'use strict';
  const qs = (s, el) => (el || document).querySelector(s);
  const qsa = (s, el) => Array.from((el || document).querySelectorAll(s));
  // Shop-wide money format (EUR, comma decimals, no trailing zeros)
  function moneyFmt(cents) {
    const amount = cents / 100;
    const str = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2).replace('.', ',');
    return '€' + str;
  }

  /* ---------- Sticky header shadow ---------- */
  const header = qs('.site-header');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Drawers ---------- */
  let lastTrigger = null;

  /* Scroll lock that also works on iOS: pin the body at its current offset and put the
     page back where it was on release. Ref-counted so nested overlays don't fight. */
  let lockedY = 0;
  let lockCount = 0;
  function lockScroll() {
    if (lockCount++ > 0) return;
    lockedY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = '-' + lockedY + 'px';
    document.body.classList.add('no-scroll');
  }
  function unlockScroll() {
    if (lockCount === 0) return;
    if (--lockCount > 0) return;
    document.body.classList.remove('no-scroll');
    document.body.style.top = '';
    // Force layout first: while the body was fixed the document had no scroll height, so a
    // restore issued before the reflow gets clamped to 0.
    void document.body.offsetHeight;
    const behavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(0, lockedY);
    document.documentElement.style.scrollBehavior = behavior;
  }

  function setDrawerOffset(drawer) {
    // Panels slide from under the header for top drawers
    if (!drawer.classList.contains('drawer--top')) return;
    const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
    drawer.style.setProperty('top', Math.max(headerBottom, 0) + 'px');
  }

  function openDrawer(id, trigger) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    lastTrigger = trigger || null;
    lockScroll();
    setDrawerOffset(drawer);
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    // Only autofocus a control that asks for it (the search field). Otherwise move focus to
    // the panel itself: screen readers still announce the dialog, but no button picks up a
    // :focus-visible ring — Safari on iOS shows one for programmatic focus after a tap.
    const explicit = qs('[data-autofocus]', drawer);
    const target = explicit || qs('.drawer__panel', drawer) || drawer;
    if (!explicit) target.setAttribute('tabindex', '-1');
    setTimeout(() => target.focus({ preventScroll: true }), 60);
    document.dispatchEvent(new CustomEvent('drawer:open', { detail: { id } }));
  }

  window.addEventListener('resize', () => {
    qsa('.drawer.is-open.drawer--top').forEach(setDrawerOffset);
  });

  function closeDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    unlockScroll();
    if (lastTrigger) { lastTrigger.focus(); lastTrigger = null; }
    document.dispatchEvent(new CustomEvent('drawer:close'));
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      const id = opener.getAttribute('data-drawer-open');
      const target = document.getElementById(id);
      if (target && target.classList.contains('is-open')) { closeDrawer(target); return; }
      openDrawer(id, opener);
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      if (!(closer.tagName === 'A' && closer.getAttribute('href'))) e.preventDefault();
      closeDrawer(closer.closest('.drawer'));
      return;
    }
    const backdrop = e.target.classList && e.target.classList.contains('drawer__backdrop');
    if (backdrop) closeDrawer(e.target.closest('.drawer'));
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDrawer(qs('.drawer.is-open'));
  });

  /* ---------- Mega menu (data-mega): drill-down on mobile, hover panes on desktop ---------- */
  const mega = qs('[data-mega]');
  if (mega) {
    const drawer = mega.closest('.drawer');
    const deskMq = window.matchMedia('(min-width: 990px)');
    const l1Items = qsa('.mega__l1-item.has-mega', mega);
    if (drawer && l1Items.length) {
      drawer.classList.add('has-mega');
      // No third level anywhere: the panel only needs L1 + rail (+ featured), so it can be narrower.
      if (!qs('.mega__l3', mega)) drawer.classList.add('mega-flat');
    }
    // Images inside the menu carry data-src only: rail thumbs + featured load when a pane opens,
    // L3 cards when their column becomes active. Nothing downloads on page load.
    // The featured promo is desktop-only, so its image never downloads on mobile.
    const hydrateRail = (pane) => {
      const sel = deskMq.matches ? '.mega__col-img, .mega__featured' : '.mega__col-img';
      qsa(sel, pane).forEach(loadSlide);
    };
    const activateCol = (col) => {
      qsa(':scope > .mega__col', col.parentElement).forEach((c) => c.classList.toggle('is-active', c === col));
      loadSlide(qs('.mega__l3', col) || col);
    };
    const activateL1 = (li) => {
      const pane = qs('.mega__l2', li);
      if (pane) hydrateRail(pane);
      l1Items.forEach((i) => {
        const on = i === li;
        i.classList.toggle('is-active', on);
        const btn = qs('.mega__l1-btn', i);
        if (btn) btn.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
      // Always (re)activate the current column so its L3 images get hydrated, even when the
      // server already marked the first column active.
      const active = qs('.mega__col.has-l3.is-active', li) || qs('.mega__col.has-l3', li);
      if (active) activateCol(active);
    };
    mega.addEventListener('click', (e) => {
      const l1 = e.target.closest('[data-mega-l1]');
      if (l1) {
        const li = l1.closest('.mega__l1-item');
        if (deskMq.matches) { activateL1(li); return; }
        li.classList.add('is-open');
        mega.classList.add('has-open');
        l1.setAttribute('aria-expanded', 'true');
        const pane = qs('.mega__l2', li);
        if (pane) { pane.scrollTop = 0; hydrateRail(pane); const back = qs('[data-mega-back]', pane); if (back) back.focus(); }
        return;
      }
      const back = e.target.closest('[data-mega-back]');
      if (back) {
        const li = back.closest('.mega__l1-item');
        li.classList.remove('is-open');
        mega.classList.remove('has-open');
        const btn = qs('.mega__l1-btn', li);
        if (btn) { btn.setAttribute('aria-expanded', 'false'); btn.focus(); }
        return;
      }
      const colBtn = e.target.closest('[data-mega-col-btn]');
      if (colBtn) {
        const col = colBtn.closest('.mega__col');
        if (deskMq.matches) { activateCol(col); return; }
        const open = col.classList.toggle('is-open');
        colBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) loadSlide(qs('.mega__l3', col) || col);
      }
    });
    if (window.matchMedia('(hover: hover)').matches) {
      l1Items.forEach((li) => li.addEventListener('mouseenter', () => { if (deskMq.matches) activateL1(li); }));
      qsa('.mega__col.has-l3', mega).forEach((col) => col.addEventListener('mouseenter', () => { if (deskMq.matches) activateCol(col); }));
    }
    document.addEventListener('drawer:open', (e) => {
      if (e.detail.id !== 'MenuDrawer' || !deskMq.matches) return;
      const current = qs('.mega__l1-item.is-current.has-mega', mega) || qs('.mega__l1-item.is-active', mega) || l1Items[0];
      if (current) activateL1(current);
    });
    // Leaving desktop: drop hover state so the mobile drill-down starts closed.
    deskMq.addEventListener('change', () => {
      if (!deskMq.matches) l1Items.forEach((i) => i.classList.remove('is-active'));
    });
  }

  /* ---------- Carousel arrows ---------- */
  qsa('[data-carousel]').forEach((wrap) => {
    const track = qs('[data-carousel-track]', wrap);
    if (!track) return;
    qsa('[data-carousel-prev]', wrap).forEach((b) => b.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' })));
    qsa('[data-carousel-next]', wrap).forEach((b) => b.addEventListener('click', () => track.scrollBy({ left: track.clientWidth, behavior: 'smooth' })));
  });

  /* ---------- Horizontal list arrows (desktop, injected into section heads) ---------- */
  const CHEV_LEFT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M15 5.5 8.5 12l6.5 6.5"/></svg>';
  const CHEV_RIGHT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="m9 5.5 6.5 6.5L9 18.5"/></svg>';
  function initHscrollNav(sec) {
    const head = qs('.section-head', sec);
    const track = qs('.hscroll', sec);
    if (!head || !track || track.closest('[data-carousel]') || qs('.hscroll-nav', head)) return;
    const nav = document.createElement('div');
    nav.className = 'hscroll-nav';
    nav.innerHTML = `<button type="button" aria-label="Previous">${CHEV_LEFT}</button><button type="button" aria-label="Next">${CHEV_RIGHT}</button>`;
    const [prev, next] = nav.children;
    const step = () => {
      const first = track.firstElementChild;
      return first ? first.getBoundingClientRect().width + 2 : track.clientWidth;
    };
    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    const update = () => {
      prev.disabled = track.scrollLeft < 10;
      next.disabled = track.scrollLeft > track.scrollWidth - track.clientWidth - 10;
    };
    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update, { passive: true });
    head.appendChild(nav);
    update();
  }
  qsa('.section').forEach(initHscrollNav);

  /* ---------- Product recommendations (replaces the server-rendered fallback) ---------- */
  const recWrap = qs('[data-recommendations]');
  if (recWrap && recWrap.dataset.productId) {
    const url = `${window.routes.root}recommendations/products?section_id=${recWrap.dataset.sectionId}&product_id=${recWrap.dataset.productId}&limit=${recWrap.dataset.limit || 6}`;
    fetch(url.replace('//recommendations', '/recommendations'))
      .then((r) => (r.ok ? r.text() : Promise.reject()))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-recommendations]');
        if (fresh && fresh.querySelector('.product-card')) {
          recWrap.innerHTML = fresh.innerHTML;
          initHscrollNav(recWrap);
          mountCardSwipe(recWrap);
          mountCardArrows(recWrap);
        }
      })
      .catch(() => {});
  }

  /* ---------- Drag-to-scroll for horizontal rows (desktop mouse) ---------- */
  const FINE_POINTER = window.matchMedia('(pointer: fine)').matches;
  function enableDragScroll(el) {
    if (!FINE_POINTER || el.dataset.dragScroll) return;
    el.dataset.dragScroll = '1';
    let down = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;
    el.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      down = true;
      moved = false;
      startX = e.pageX;
      startLeft = el.scrollLeft;
    });
    el.addEventListener('mousemove', (e) => {
      if (!down) return;
      const dx = e.pageX - startX;
      if (!moved && Math.abs(dx) > 5) {
        moved = true;
        el.classList.add('is-dragging');
      }
      if (moved) {
        el.scrollLeft = startLeft - dx;
        e.preventDefault();
      }
    });
    const endDrag = () => {
      if (!down) return;
      down = false;
      el.classList.remove('is-dragging');
    };
    el.addEventListener('mouseup', endDrag);
    el.addEventListener('mouseleave', endDrag);
    el.addEventListener('click', (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    }, true);
    el.addEventListener('dragstart', (e) => e.preventDefault());
  }
  function initDragScroll() {
    qsa('.hscroll, .pdp-series__row, .cart-upsell__row, .lightbox__track').forEach(enableDragScroll);
  }
  initDragScroll();

  /* ---------- Product card image swipe (lazy slides + dots) ---------- */
  // Slides after the first carry data-src/data-srcset only; swap them in when the slide is
  // scrolled into the track's viewport (IntersectionObserver rooted on the track) or on first
  // hover/touch (preload slide 2 so the first swipe is instant).
  function loadSlide(slide) {
    qsa('img[data-src]', slide).forEach((img) => {
      img.srcset = img.dataset.srcset || '';
      img.src = img.dataset.src;
      delete img.dataset.src;
      delete img.dataset.srcset;
    });
  }
  // Mouse drag for card tracks: 1:1 while dragging (snap off), then a smooth settle to the
  // nearest slide -- a flick past 20% of the width (min 40px) advances one slide. Re-enabling
  // mandatory snap directly after a drag makes the browser jump instead of glide.
  function enableCardDrag(track) {
    if (!FINE_POINTER) return;
    let down = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;
    let settleTimer = 0;
    const settle = (dx) => {
      const w = track.clientWidth || 1;
      const max = track.children.length - 1;
      let idx = Math.round(track.scrollLeft / w);
      const from = Math.round(startLeft / w);
      if (idx === from && Math.abs(dx) > Math.max(40, w * 0.2)) idx = from + (dx < 0 ? 1 : -1);
      idx = Math.max(0, Math.min(max, idx));
      track.classList.add('is-settling');
      track.scrollTo({ left: idx * w, behavior: 'smooth' });
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => track.classList.remove('is-settling'), 500);
    };
    track.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      down = true;
      moved = false;
      startX = e.pageX;
      startLeft = track.scrollLeft;
      clearTimeout(settleTimer);
      track.classList.add('is-settling');
    });
    window.addEventListener('mousemove', (e) => {
      if (!down) return;
      const dx = e.pageX - startX;
      if (!moved && Math.abs(dx) > 4) {
        moved = true;
        track.classList.add('is-dragging');
      }
      if (moved) {
        track.scrollLeft = startLeft - dx;
        e.preventDefault();
      }
    });
    const endDrag = (e) => {
      if (!down) return;
      down = false;
      track.classList.remove('is-dragging');
      if (moved) settle(e.pageX - startX);
      else track.classList.remove('is-settling');
    };
    window.addEventListener('mouseup', endDrag);
    track.addEventListener('click', (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    }, true);
    track.addEventListener('dragstart', (e) => e.preventDefault());
  }
  function mountCardSwipe(root) {
    qsa('[data-card-swipe]', root).forEach((track) => {
      if (track.dataset.swipeReady) return;
      track.dataset.swipeReady = '1';
      const slides = Array.from(track.children);
      const dots = qsa('i', track.parentElement.querySelector('.product-card__dots'));
      enableCardDrag(track);
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (!en.isIntersecting) return;
            loadSlide(en.target);
            io.unobserve(en.target);
          });
        }, { root: track, threshold: 0.05 });
        slides.slice(1).forEach((sl) => io.observe(sl));
      } else {
        slides.slice(1).forEach(loadSlide);
      }
      const warm = () => { if (slides[1]) loadSlide(slides[1]); };
      track.addEventListener('pointerenter', warm, { once: true, passive: true });
      track.addEventListener('touchstart', warm, { once: true, passive: true });
      if (dots.length) {
        let raf = 0;
        track.addEventListener('scroll', () => {
          if (raf) return;
          raf = requestAnimationFrame(() => {
            raf = 0;
            const i = Math.round(track.scrollLeft / track.clientWidth);
            dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
          });
        }, { passive: true });
      }
    });
  }
  mountCardSwipe();

  // Horizontal rows use a static track: images switch only via desktop hover arrows (wrapping),
  // never by touch/drag, so the row's own horizontal scroll stays conflict-free.
  function mountCardArrows(root) {
    qsa('[data-card-arrows]', root).forEach((track) => {
      if (track.dataset.arrowsReady) return;
      track.dataset.arrowsReady = '1';
      const slides = Array.from(track.children);
      qsa('[data-card-arrow]', track.parentElement).forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const w = track.clientWidth || 1;
          let i = Math.round(track.scrollLeft / w) + (btn.dataset.cardArrow === 'next' ? 1 : -1);
          if (i < 0) i = slides.length - 1;
          if (i >= slides.length) i = 0;
          loadSlide(slides[i]);
          track.scrollTo({ left: i * w, behavior: 'smooth' });
        });
      });
      // Warm the second image on first hover so the first arrow click is instant
      track.parentElement.addEventListener('mouseenter', () => { if (slides[1]) loadSlide(slides[1]); }, { once: true });
    });
  }
  mountCardArrows();

  /* The footer form posting successfully counts as a signup too, so the popup
     stops asking. Shares the same flag the popup reads. */
  (function footerNewsletterFlag() {
    if (!qs('.footer-news__success')) return;
    try { window.localStorage.setItem('newsletter_subscribed', '1'); } catch (e) { /* private mode */ }
    document.dispatchEvent(new CustomEvent('newsletter:subscribed', { detail: { source: 'footer' } }));
  })();

  /* ---------- Newsletter pop-up ----------
     Same model as Next Step's Subscribe Pop-up: one shared localStorage flag
     (`newsletter_subscribed`) keeps the popup and the footer form in sync, plus a
     per-popup dismissal stamp so a visitor who closes it is left alone for N days. */
  (function promoPopup() {
    const popup = qs('[data-promo-popup]');
    if (!popup) return;
    const SUBSCRIBED = 'newsletter_subscribed';
    const dismissKey = 'coolchap_popup_' + popup.id;
    const store = {
      get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
      set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* private mode */ } },
    };

    const success = qs('[data-promo-success]', popup);
    if (success) store.set(SUBSCRIBED, '1');

    let lastFocus = null;
    const open = () => {
      lastFocus = document.activeElement;
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      const first = qs('input[type="email"]', popup) || qs('[data-promo-close]', popup);
      if (first) first.focus({ preventScroll: true });
    };
    const close = () => {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      store.set(dismissKey, String(Date.now()));
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    };

    popup.addEventListener('click', (e) => { if (e.target.closest('[data-promo-close]')) close(); });
    document.addEventListener('keyup', (e) => {
      if (e.code === 'Escape' && popup.classList.contains('is-open')) close();
    });
    // Another component (footer form, cart) reported a signup: never show again.
    document.addEventListener('newsletter:subscribed', () => {
      store.set(SUBSCRIBED, '1');
      if (popup.classList.contains('is-open')) close();
    });

    // A form posted successfully: flag it, tell the rest of the page, then close.
    if (success) {
      document.dispatchEvent(new CustomEvent('newsletter:subscribed', { detail: { source: 'popup' } }));
      open();
      setTimeout(close, 4000);
      return;
    }

    if (store.get(SUBSCRIBED)) return;
    const days = Number(popup.dataset.rememberDays) || 30;
    const since = Number(store.get(dismissKey));
    if (since && Date.now() - since < days * 864e5) return;

    // In the theme editor the merchant needs to see it while the section is selected.
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', (e) => {
        if (e.detail.sectionId === popup.dataset.sectionId) open();
      });
      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.detail.sectionId === popup.dataset.sectionId) {
          popup.classList.remove('is-open');
          popup.setAttribute('aria-hidden', 'true');
        }
      });
      return;
    }

    const delay = (Number(popup.dataset.delay) || 0) * 1000;
    const openWhenFree = () => {
      // Never stack on top of an open drawer (menu, search, cart): wait until it closes.
      if (qs('.drawer.is-open')) {
        document.addEventListener('drawer:close', openWhenFree, { once: true });
        return;
      }
      open();
    };
    setTimeout(openWhenFree, delay);
  })();

  /* ---------- Collection view switcher ----------
     Ported from Next Step: the buttons toggle <html data-collection-view> and the choice is
     remembered in localStorage; the section applies it inline before paint to avoid a flash. */
  (function collectionViews() {
    const bar = qs('.view-switch');
    if (!bar) return;
    const KEY = 'collection_view';
    const sync = () => {
      const view = document.documentElement.getAttribute('data-collection-view');
      qsa('[data-view-btn]').forEach((b) => {
        b.setAttribute('aria-pressed', b.dataset.viewBtn === view ? 'true' : 'false');
      });
    };
    bar.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-view-btn]');
      if (!btn) return;
      document.documentElement.setAttribute('data-collection-view', btn.dataset.viewBtn);
      try { window.localStorage.setItem(KEY, btn.dataset.viewBtn); } catch (err) { /* private mode */ }
      sync();
      // Column widths changed: let the card carousels re-measure.
      window.dispatchEvent(new Event('resize'));
    });
    sync();
  })();

  /* ---------- Collection "Load more": append the next page without leaving the page ----------
     The button stays a real <a href> to the next page, so crawlers and no-JS visitors still
     reach every page; JS only intercepts the click. */
  (function collectionLoadMore() {
    const wrap = qs('[data-load-more-wrap]');
    const grid = qs('.product-grid');
    if (!wrap || !grid) return;
    wrap.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-load-more]');
      if (!btn || btn.dataset.busy) return;
      e.preventDefault();
      const url = btn.getAttribute('href');
      btn.dataset.busy = '1';
      btn.classList.add('is-loading');
      fetch(url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + btn.dataset.section)
        .then((r) => (r.ok ? r.text() : Promise.reject()))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('.product-grid');
          if (!fresh || !fresh.querySelector('.product-card')) return Promise.reject();
          const first = fresh.firstElementChild;
          const frag = document.createDocumentFragment();
          Array.from(fresh.children).forEach((node) => frag.appendChild(node));
          grid.appendChild(frag);
          mountCardSwipe(grid);
          mountCardArrows(grid);
          const next = doc.querySelector('[data-load-more]');
          if (next) {
            btn.setAttribute('href', next.getAttribute('href'));
            delete btn.dataset.busy;
            btn.classList.remove('is-loading');
          } else {
            wrap.remove();
          }
          if (first) first.setAttribute('tabindex', '-1');
          if (first) first.focus({ preventScroll: true });
        })
        .catch(() => { window.location.href = url; });
    });
  })();

  /* One-time swipe hint on collection pages: the first card with several images peeks 50% to the
     second image and slides back, once per browser session (sessionStorage). */
  (function swipeHint() {
    const KEY = 'coolchap_swipe_hint';
    const grid = qs('[data-swipe-hint]');
    if (!grid || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    try { if (sessionStorage.getItem(KEY)) return; } catch (e) { return; }
    const track = qs('[data-card-swipe]', grid);
    if (!track) return;
    const done = () => { try { sessionStorage.setItem(KEY, '1'); } catch (e) { /* noop */ } };
    // User already swiping -> no need to hint.
    const cancel = () => { done(); io.disconnect(); };
    track.addEventListener('pointerdown', cancel, { once: true, passive: true });
    track.addEventListener('touchstart', cancel, { once: true, passive: true });
    const play = () => {
      done();
      track.classList.add('is-peeking');
      setTimeout(() => track.classList.remove('is-peeking'), 900);
    };
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((en) => en.isIntersecting)) return;
      io.disconnect();
      const next = track.children[1];
      loadSlide(next);
      const img = qs('img', next);
      const start = () => setTimeout(play, 500);
      if (img && !img.complete) {
        img.addEventListener('load', start, { once: true });
        img.addEventListener('error', start, { once: true });
      } else {
        start();
      }
    }, { threshold: 0.6 });
    io.observe(track);
  })();

  /* ---------- PDP gallery: touch swipe that never steals the page scroll ---------- */
  // A native horizontal scroller locks the gesture axis as soon as a finger lands on it, so on
  // phones a swipe that starts on the photo pages the gallery instead of scrolling the page.
  // Here the browser keeps the vertical axis only (touch-action: pan-y) and the horizontal drag
  // runs from these handlers: snap off while dragging, smooth settle to the nearest photo after.
  function enableGallerySwipe(track) {
    if (FINE_POINTER || track.dataset.swipeTouch) return;
    track.dataset.swipeTouch = '1';
    track.style.touchAction = 'pan-y pinch-zoom';
    if (!track.style.touchAction) track.style.touchAction = 'pan-y';
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let dx = 0;
    let axis = '';
    let swiped = false;
    let settleTimer = 0;
    const settle = () => {
      const w = track.clientWidth || 1;
      const max = track.children.length - 1;
      const from = Math.round(startLeft / w);
      let idx = Math.round(track.scrollLeft / w);
      if (idx === from && Math.abs(dx) > Math.max(40, w * 0.2)) idx = from + (dx < 0 ? 1 : -1);
      idx = Math.max(0, Math.min(max, idx));
      track.scrollTo({ left: idx * w, behavior: 'smooth' });
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => track.classList.remove('is-settling'), 450);
    };
    track.addEventListener('touchstart', (e) => {
      axis = '';
      swiped = false;
      if (e.touches.length !== 1) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startLeft = track.scrollLeft;
      dx = 0;
      clearTimeout(settleTimer);
    }, { passive: true });
    track.addEventListener('touchmove', (e) => {
      if (axis === 'y' || e.touches.length !== 1) return;
      dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (!axis) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (axis !== 'x') return;
        track.classList.add('is-settling');
      }
      if (e.cancelable) e.preventDefault();
      track.scrollLeft = startLeft - dx;
    }, { passive: false });
    const endSwipe = () => {
      if (axis === 'x') {
        swiped = Math.abs(dx) > 6;
        settle();
      }
      axis = '';
    };
    track.addEventListener('touchend', endSwipe);
    track.addEventListener('touchcancel', endSwipe);
    // A finished swipe must not open the lightbox on the photo it landed on.
    track.addEventListener('click', (e) => {
      if (!swiped) return;
      swiped = false;
      e.preventDefault();
      e.stopPropagation();
    }, true);
  }

  /* ---------- Gallery position indicator + media/UGC panes ---------- */
  qsa('[data-gallery]').forEach((gallery) => {
    const tracks = qsa('[data-gallery-track]', gallery);
    const bar = qs('[data-gallery-bar]', gallery);
    if (!tracks.length) return;
    const activeTrack = () => tracks.find((t) => !t.hidden) || tracks[0];
    const syncBar = () => {
      if (!bar) return;
      const track = activeTrack();
      const count = track.children.length || 1;
      bar.style.width = (100 / count) + '%';
      const i = Math.round(track.scrollLeft / track.clientWidth);
      bar.style.transform = `translateX(${i * 100}%)`;
    };
    tracks.forEach((track) => {
      enableGallerySwipe(track);
      track.addEventListener('scroll', () => requestAnimationFrame(() => {
        if (!track.hidden) syncBar();
      }), { passive: true });
    });
    qsa('[data-pane-btn]', gallery).forEach((btn) => {
      btn.addEventListener('click', () => {
        const pane = btn.dataset.paneBtn;
        qsa('[data-pane-btn]', gallery).forEach((b) => {
          const on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        tracks.forEach((t) => { t.hidden = t.dataset.pane !== pane; });
        const track = activeTrack();
        track.scrollTo({ left: 0 });
        syncBar();
      });
    });
    syncBar();
  });

  /* ---------- Cart API ---------- */
  const Cart = {
    async add(items) {
      const res = await fetch(window.routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw await res.json();
      return res.json();
    },
    async change(line, quantity) {
      const res = await fetch(window.routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      if (!res.ok) throw await res.json();
      return res.json();
    },
    async refreshSections(names) {
      const url = `${window.location.pathname}?sections=${names.join(',')}`;
      const res = await fetch(url);
      return res.json();
    },
    updateCount(count) {
      qsa('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.setAttribute('data-count', count);
      });
    },
    async refreshDrawer() {
      const drawer = document.getElementById('CartDrawer');
      if (!drawer) return;
      const res = await fetch(`${window.routes.root}?sections=cart-drawer`);
      const sections = await res.json();
      const holder = document.createElement('div');
      holder.innerHTML = sections['cart-drawer'];
      const freshPanel = holder.querySelector('[data-cart-drawer-panel]');
      const panel = qs('[data-cart-drawer-panel]', drawer);
      if (freshPanel && panel) panel.innerHTML = freshPanel.innerHTML;
      loadCartUpsell();
    },
  };
  window.CoolchapCart = Cart;

  /* ---------- Cart drawer ---------- */
  async function syncCartCount() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      Cart.updateCount(cart.item_count);
    } catch (err) { /* noop */ }
  }
  async function openCartDrawer(trigger, opts = {}) {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) { window.location.href = window.routes.cart; return; }
    if (opts.refreshFirst) {
      try { await Cart.refreshDrawer(); } catch (err) { /* open anyway */ }
      openDrawer('CartDrawer', trigger || null);
    } else {
      openDrawer('CartDrawer', trigger || null);
      Cart.refreshDrawer().catch(() => {});
    }
    syncCartCount();
  }
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener) { e.preventDefault(); openCartDrawer(opener); }
  });

  /* Drawer quantity / remove (event delegation on the drawer) */
  document.addEventListener('click', async (e) => {
    const drawer = e.target.closest('#CartDrawer');
    if (!drawer) return;
    const dec = e.target.closest('[data-qty-dec]');
    const inc = e.target.closest('[data-qty-inc]');
    const rm = e.target.closest('[data-line-remove]');
    const target = dec || inc || rm;
    if (!target) return;
    e.preventDefault();
    const line = Number(target.dataset.line);
    const input = qs(`[data-qty-input][data-line="${line}"]`, drawer);
    const current = input ? Number(input.value) : 1;
    const nextQty = rm ? 0 : Math.max(0, current + (inc ? 1 : -1));
    const panel = qs('[data-cart-drawer-panel]', drawer);
    if (panel) panel.classList.add('is-busy');
    try {
      const cart = await Cart.change(line, nextQty);
      Cart.updateCount(cart.item_count);
      await Cart.refreshDrawer();
      const pageRoot = qs('[data-cart-root]');
      if (pageRoot) {
        const sections = await Cart.refreshSections(['main-cart']);
        const holder = document.createElement('div');
        holder.innerHTML = sections['main-cart'];
        const fresh = holder.querySelector('[data-cart-root]');
        if (fresh) pageRoot.replaceWith(fresh);
      }
    } catch (err) {
      window.location.reload();
    } finally {
      if (panel) panel.classList.remove('is-busy');
    }
  });

  /* ---------- Product form ---------- */
  qsa('[data-product-form]').forEach((root) => {
    const dataEl = qs('[data-product-json]', root);
    if (!dataEl) return;
    const product = JSON.parse(dataEl.textContent);
    const priceEl = qs('[data-pdp-price]', root);
    const stockEl = qs('[data-pdp-stock]', root);
    const idInput = qs('input[name="id"]', root);
    const btn = qs('[data-atc]', root);
    const btnLabel = btn ? qs('[data-atc-label]', btn) : null;

    // Option inputs are radios (pills) or <select>s (dropdown picker); both carry data-option-input.
    function selectedValue(position) {
      const sel = qs(`select[data-option-input][data-option-position="${position}"]`, root);
      if (sel) return sel.value;
      const c = qs(`[data-option-input][data-option-position="${position}"]:checked`, root);
      return c ? c.value : null;
    }
    function optionPositions() {
      const inputs = qsa('[data-option-input]', root);
      return [...new Set(inputs.map((i) => Number(i.dataset.optionPosition)))].sort();
    }
    function currentVariant() {
      const positions = optionPositions();
      if (!positions.length) return product.variants[0] || null;
      const selected = positions.map(selectedValue);
      if (selected.some((v) => v === null)) return null;
      return product.variants.find((v) => selected.every((val, i) => v.options[i] === val)) || null;
    }

    const fmtMoney = moneyFmt;

    function refreshPillStates() {
      const positions = optionPositions();
      if (!positions.length) return;
      const purchasable = (p, value) => {
        const candidate = positions.map((q) => (q === p ? value : selectedValue(q)));
        return product.variants.some(
          (v) => v.available && candidate.every((val, i) => val === null || v.options[i] === val)
        );
      };
      positions.forEach((p) => {
        qsa(`input[data-option-input][data-option-position="${p}"]`, root).forEach((input) => {
          input.disabled = !purchasable(p, input.value);
        });
        qsa(`select[data-option-input][data-option-position="${p}"] option`, root).forEach((opt) => {
          opt.disabled = !purchasable(p, opt.value);
        });
      });
    }

    const stickyBar = qs('[data-sticky-atc]', root);
    const stickyPrice = qs('[data-pdp-price-sticky]', root);
    const buyRow = qs('.pdp-buy', root);
    if (stickyBar && buyRow && 'IntersectionObserver' in window) {
      let passedBuy = false;
      let footerInView = false;
      const applySticky = () => {
        const show = passedBuy && !footerInView;
        stickyBar.classList.toggle('is-visible', show);
        stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
      };
      const buyIo = new IntersectionObserver(([entry]) => {
        passedBuy = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        applySticky();
      }, { threshold: 0 });
      buyIo.observe(buyRow);
      const footerEl = qs('.site-footer');
      if (footerEl) {
        const footIo = new IntersectionObserver(([entry]) => {
          footerInView = entry.isIntersecting;
          applySticky();
        }, { threshold: 0 });
        footIo.observe(footerEl);
      }
    }

    const compareEl = priceEl ? qs('[data-pdp-compare]', priceEl) : null;
    const currentEl = priceEl ? qs('[data-pdp-current]', priceEl) : null;
    const skuWrap = qs('[data-pdp-sku-wrap]', root);
    const inventoryEl = qs('[data-pdp-inventory]', root);
    function renderInventory(v) {
      if (!inventoryEl) return;
      const threshold = Number(inventoryEl.dataset.threshold) || 0;
      const base = Number(inventoryEl.dataset.base) || 20;
      const low = v.inventory_quantity <= threshold;
      const show = v.inventory_tracked && v.available && (inventoryEl.dataset.mode === 'always' || low);
      inventoryEl.hidden = !show;
      inventoryEl.classList.toggle('is-low', low);
      const text = qs('[data-inventory-text]', inventoryEl);
      if (text) {
        if (low) {
          text.textContent = inventoryEl.dataset.showCount === 'true'
            ? inventoryEl.dataset.labelLow.replace('__count__', v.inventory_quantity)
            : inventoryEl.dataset.labelLowNocount;
        } else {
          text.textContent = inventoryEl.dataset.labelOk;
        }
      }
      const bar = qs('[data-inventory-bar]', inventoryEl);
      if (bar) bar.style.width = Math.max(4, Math.min(100, Math.round((v.inventory_quantity / base) * 100))) + '%';
    }

    function update() {
      refreshPillStates();
      const v = currentVariant();
      if (!v) return;
      if (idInput) idInput.value = v.id;
      if (priceEl) {
        if (currentEl) {
          currentEl.textContent = fmtMoney(v.price);
          const onSale = priceEl.dataset.showCompare === 'true' && v.compare_at_price > v.price;
          currentEl.classList.toggle('is-sale', onSale);
          if (compareEl) {
            compareEl.hidden = !onSale;
            compareEl.textContent = onSale ? fmtMoney(v.compare_at_price) : '';
          }
        } else {
          priceEl.textContent = fmtMoney(v.price);
        }
      }
      if (stickyPrice) stickyPrice.textContent = fmtMoney(v.price);
      if (skuWrap) {
        const skuEl = qs('[data-pdp-sku]', skuWrap);
        if (skuEl) skuEl.textContent = v.sku || '';
        skuWrap.hidden = !v.sku;
      }
      renderInventory(v);
      optionPositions().forEach((p) => {
        const lbl = qs(`[data-option-label="${p}"]`, root);
        if (lbl) lbl.textContent = selectedValue(p) || '';
      });
      if (btn) {
        btn.disabled = !v.available;
        if (btnLabel) btnLabel.textContent = v.available ? btn.dataset.labelAdd : btn.dataset.labelSoldout;
      }
      if (stockEl) {
        const tpl = v.available ? stockEl.dataset.instock : stockEl.dataset.outstock;
        const span = qs('[data-stock-text]', stockEl);
        if (span) span.textContent = tpl.replace('__variant__', v.title);
        const delivery = qs('[data-stock-delivery]', stockEl);
        if (delivery) delivery.hidden = !v.available;
        stockEl.hidden = !span && !v.available;
      }
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      } catch (err) { /* noop */ }
    }

    const errorEl = qs('[data-atc-error]', root);
    root.addEventListener('change', (e) => {
      if (!e.target.matches('[data-option-input]')) return;
      if (errorEl) errorEl.hidden = true;
      qsa('[data-needs-variant]', root).forEach((el) => el.removeAttribute('data-needs-variant'));
      update();
    });
    root.addEventListener('submit', async (e) => {
      if (!e.target.matches('form[data-atc-form]')) return;
      e.preventDefault();
      if (errorEl) errorEl.hidden = true;
      const v = currentVariant();
      if (!v) {
        // Nothing picked yet: say so, nudge the pills, and put focus on the first one.
        const grid = qs('.size-grid, .pdp-select', root);
        if (errorEl) {
          errorEl.textContent = (btn && btn.dataset.labelChoose) || 'Please choose a size';
          errorEl.hidden = false;
        }
        if (grid) { grid.classList.add('shake'); setTimeout(() => grid.classList.remove('shake'), 500); }
        const firstPill = qs('.size-grid input:not(:disabled), .pdp-select select', root);
        if (firstPill) firstPill.focus({ preventScroll: true });
        if (grid) grid.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }
      const qtyInput = qs('input[name="quantity"]', root);
      const quantity = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
      // Line item properties from custom_option blocks (checkbox falls back to its hidden "unchecked" value)
      const properties = {};
      qsa('[data-line-property]', root).forEach((el) => {
        if (el.type === 'radio') { if (el.checked) properties[el.name.slice(11, -1)] = el.value; return; }
        if (el.type === 'checkbox') {
          const def = qs(`[data-line-property-default][name="${el.name}"]`, root);
          properties[el.name.slice(11, -1)] = el.checked ? el.value : (def ? def.value : '');
          return;
        }
        if (el.value !== '') properties[el.name.slice(11, -1)] = el.value;
      });
      const main = { id: v.id, quantity };
      if (Object.keys(properties).length) main.properties = properties;
      const items = [main];
      qsa('[data-accessory]:checked', root).forEach((acc) => items.push({ id: Number(acc.value), quantity: 1 }));
      if (btn) { btn.disabled = true; if (btnLabel) btnLabel.textContent = btn.dataset.labelAdding; }
      try {
        await Cart.add(items);
        await openCartDrawer(btn, { refreshFirst: true });
        if (btn) { btn.disabled = false; if (btnLabel) btnLabel.textContent = btn.dataset.labelAdd; }
      } catch (err) {
        if (btn) { btn.disabled = false; if (btnLabel) btnLabel.textContent = btn.dataset.labelAdd; }
        if (errorEl) {
          errorEl.textContent = (err && (err.description || err.message)) || 'Something went wrong. Please try again.';
          errorEl.hidden = false;
        }
      }
    });

    update();
  });

  /* ---------- PDP blocks: quantity, countdown, read more, share, complementary, open-on-desktop ---------- */
  // The product form lives at the top of the info column (blocks point at it via form="");
  // move the dynamic checkout button it rendered into the buy block's slot.
  (function () {
    const src = qs('[data-dynamic-src]');
    const slot = qs('[data-dynamic-slot]');
    if (src && slot) { while (src.firstChild) slot.appendChild(src.firstChild); }
  })();
  qsa('[data-qty]').forEach((wrap) => {
    const input = qs('input', wrap);
    const step = (d) => { input.value = Math.max(1, (parseInt(input.value, 10) || 1) + d); input.dispatchEvent(new Event('change', { bubbles: true })); };
    const minus = qs('[data-qty-minus]', wrap);
    const plus = qs('[data-qty-plus]', wrap);
    if (minus) minus.addEventListener('click', () => step(-1));
    if (plus) plus.addEventListener('click', () => step(1));
  });

  qsa('[data-countdown]').forEach((el) => {
    const end = new Date(el.dataset.end).getTime();
    if (Number.isNaN(end)) return;
    const cells = { d: qs('[data-cd="d"]', el), h: qs('[data-cd="h"]', el), m: qs('[data-cd="m"]', el), s: qs('[data-cd="s"]', el) };
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      let diff = Math.max(0, Math.floor((end - Date.now()) / 1000));
      if (diff === 0 && el.dataset.hideExpired === 'true') { el.hidden = true; clearInterval(timer); return; }
      const d = Math.floor(diff / 86400); diff -= d * 86400;
      const h = Math.floor(diff / 3600); diff -= h * 3600;
      const m = Math.floor(diff / 60); const sec = diff - m * 60;
      if (cells.d) cells.d.textContent = d;
      if (cells.h) cells.h.textContent = pad(h);
      if (cells.m) cells.m.textContent = pad(m);
      if (cells.s) cells.s.textContent = pad(sec);
      el.hidden = false;
    };
    tick();
    const timer = setInterval(tick, 1000);
  });

  qsa('[data-readmore]').forEach((wrap) => {
    const body = qs('[data-readmore-body]', wrap);
    const btn = qs('[data-readmore-btn]', wrap);
    if (!body || !btn) return;
    const check = () => {
      const more = body.scrollHeight > body.clientHeight + 4;
      wrap.classList.toggle('has-more', more || wrap.classList.contains('is-open'));
      btn.hidden = !(more || wrap.classList.contains('is-open'));
    };
    btn.addEventListener('click', () => {
      const open = wrap.classList.toggle('is-open');
      btn.textContent = open ? btn.dataset.less : btn.dataset.more;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    check();
    window.addEventListener('resize', check, { passive: true });
  });

  qsa('[data-share]').forEach((wrap) => {
    const copyBtn = qs('[data-share-copy]', wrap);
    if (copyBtn && navigator.clipboard) {
      copyBtn.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(window.location.href.split('?')[0]);
          const label = qs('span', copyBtn);
          const original = label ? label.textContent : '';
          if (label) label.textContent = copyBtn.dataset.copied;
          setTimeout(() => { if (label) label.textContent = original; }, 1800);
        } catch (e) { /* noop */ }
      });
    }
    const nativeBtn = qs('[data-share-native]', wrap);
    if (nativeBtn && navigator.share) {
      nativeBtn.hidden = false;
      nativeBtn.addEventListener('click', () => {
        navigator.share({ title: wrap.dataset.title || document.title, url: window.location.href }).catch(() => {});
      });
    }
  });

  qsa('[data-complementary]').forEach((wrap) => {
    const list = qs('[data-complementary-list]', wrap);
    if (!list) return;
    const url = `${window.routes.root}recommendations/products.json?product_id=${wrap.dataset.productId}&limit=${wrap.dataset.limit || 3}&intent=${wrap.dataset.intent || 'complementary'}`;
    fetch(url.replace('//recommendations', '/recommendations'))
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        const products = (data.products || []).slice(0, Number(wrap.dataset.limit) || 3);
        if (!products.length) { wrap.hidden = true; return; }
        list.innerHTML = products.map((p) => {
          const img = p.featured_image ? p.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_160x160$1$2') : '';
          const compare = p.compare_at_price > p.price ? `<s class="text-soft">${moneyFmt(p.compare_at_price)}</s> ` : '';
          return `<li class="pdp-comp__item">
            <a class="pdp-comp__thumb" href="${p.url}">${img ? `<img src="${img}" alt="" width="80" height="80" loading="lazy">` : ''}</a>
            <span class="pdp-comp__meta"><a href="${p.url}">${p.title}</a><span>${compare}${moneyFmt(p.price)}</span></span>
            ${p.available ? `<button type="button" class="pdp-comp__add" data-quick-add="${p.handle}" aria-label="${wrap.dataset.addLabel || '+'}">${'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>'}</button>` : ''}
          </li>`;
        }).join('');
      })
      .catch(() => { wrap.hidden = true; });
  });

  // Collapsible rows flagged "open on desktop" open only at desktop widths.
  const openDesktop = qsa('details[data-open-desktop]');
  if (openDesktop.length) {
    const mq = window.matchMedia('(min-width: 990px)');
    const sync = () => openDesktop.forEach((d) => { if (mq.matches) d.open = true; else if (!d.hasAttribute('data-open-mobile')) d.open = false; });
    sync();
    mq.addEventListener('change', sync);
  }

  /* ---------- Cart page quantity / remove ---------- */
  const cartRoot = qs('[data-cart-root]');
  if (cartRoot) {
    async function setQty(line, qty) {
      cartRoot.classList.add('is-busy');
      try {
        const cart = await Cart.change(line, qty);
        Cart.updateCount(cart.item_count);
        const sections = await Cart.refreshSections(['main-cart']);
        const holder = document.createElement('div');
        holder.innerHTML = sections['main-cart'];
        const fresh = holder.querySelector('[data-cart-root]');
        if (fresh) cartRoot.replaceWith(fresh);
        document.dispatchEvent(new Event('cart:rerendered'));
      } catch (e) {
        window.location.reload();
      }
    }
    document.addEventListener('click', (e) => {
      const dec = e.target.closest('[data-qty-dec]');
      const inc = e.target.closest('[data-qty-inc]');
      const rm = e.target.closest('[data-line-remove]');
      const target = dec || inc || rm;
      if (!target || !target.closest('[data-cart-root]')) return;
      e.preventDefault();
      const line = Number(target.dataset.line);
      const input = qs(`[data-qty-input][data-line="${line}"]`);
      const current = input ? Number(input.value) : 1;
      if (rm) return setQty(line, 0);
      setQty(line, Math.max(0, current + (inc ? 1 : -1)));
    });
  }

  /* ---------- Quick add (product cards) ---------- */
  const quickSheet = document.getElementById('QuickAddSheet');
  if (quickSheet) {
    const quickBody = qs('[data-quick-body]', quickSheet);
    const quickAtc = qs('[data-quick-atc]', quickSheet);
    let quickSelected = null;
    const money = (cents) => {
      const amount = cents / 100;
      return '€' + (amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2).replace('.', ','));
    };
    async function quickAddVariant(vid) {
      quickAtc.disabled = true;
      quickAtc.textContent = quickAtc.dataset.labelAdding;
      try {
        await Cart.add([{ id: vid, quantity: 1 }]);
        closeDrawer(quickSheet);
        await openCartDrawer(null, { refreshFirst: true });
      } finally {
        quickAtc.textContent = quickAtc.dataset.labelAdd;
        quickAtc.disabled = quickSelected === null;
      }
    }
    quickAtc.addEventListener('click', () => {
      if (quickSelected !== null) quickAddVariant(quickSelected);
    });
    document.addEventListener('click', async (e) => {
      const qbtn = e.target.closest('[data-quick-add]');
      if (!qbtn) return;
      e.preventDefault();
      e.stopPropagation();
      qbtn.disabled = true;
      try {
        const res = await fetch(`${window.routes.root}products/${qbtn.dataset.quickAdd}.js`.replace(/\/\/products/, '/products'));
        if (!res.ok) return;
        const p = await res.json();
        const purchasable = p.variants.filter((v) => v.available);
        if (!purchasable.length) return;
        if (p.variants.length === 1) {
          quickSelected = p.variants[0].id;
          await quickAddVariant(quickSelected);
          quickSelected = null;
          return;
        }
        quickSelected = null;
        quickAtc.disabled = true;
        quickAtc.textContent = quickAtc.dataset.labelAdd;
        quickBody.innerHTML = '';
        const head = document.createElement('div');
        head.className = 'quick-sheet__product';
        const nameEl = document.createElement('strong');
        nameEl.textContent = p.title;
        const priceHint = document.createElement('span');
        priceHint.textContent = money(purchasable[0].price);
        head.append(nameEl, priceHint);
        const grid = document.createElement('div');
        grid.className = 'quick-sheet__grid';
        p.variants.forEach((v) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'quick-size';
          b.textContent = v.title;
          b.disabled = !v.available;
          b.addEventListener('click', () => {
            quickSelected = v.id;
            priceHint.textContent = money(v.price);
            qsa('.quick-size', grid).forEach((x) => x.classList.toggle('is-selected', x === b));
            quickAtc.disabled = false;
          });
          grid.appendChild(b);
        });
        quickBody.append(head, grid);
        openDrawer('QuickAddSheet', qbtn);
      } finally {
        qbtn.disabled = false;
      }
    });
  }

  /* ---------- Cart drawer upsell (native recommendations) ---------- */
  const fmtEuro = (cents) => {
    const amount = cents / 100;
    return '€' + (amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2).replace('.', ','));
  };
  const escHtml = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  async function loadCartUpsell() {
    const wrap = qs('[data-cart-upsell]');
    if (!wrap) return;
    const pid = wrap.dataset.sourceProduct;
    if (!pid) { wrap.hidden = true; return; }
    try {
      const url = `${window.routes.root}recommendations/products.json?product_id=${pid}&limit=6`.replace(/\/\/recommendations/, '/recommendations');
      const [recRes, cartRes] = await Promise.all([fetch(url), fetch('/cart.js')]);
      if (!recRes.ok || !cartRes.ok) return;
      const data = await recRes.json();
      const cart = await cartRes.json();
      const inCart = new Set(cart.items.map((i) => i.product_id));
      const prods = (data.products || []).filter((p) => p.available && !inCart.has(p.id)).slice(0, 4);
      const row = qs('[data-cart-upsell-row]', wrap);
      if (!prods.length || !row) { wrap.hidden = true; return; }
      row.innerHTML = prods.map((p) => {
        const img = p.featured_image
          ? `<img src="${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=240" alt="" loading="lazy">`
          : '';
        return `<div class="cart-upsell__card">
          <div class="cart-upsell__top">
            <a href="${p.url}"><span class="cart-upsell__media">${img}</span></a>
            <button type="button" class="product-card__quick" data-quick-add="${p.handle}" aria-label="+">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M12 4v16M4 12h16"/></svg>
            </button>
          </div>
          <a href="${p.url}">
            <span class="cart-upsell__title">${escHtml(p.title)}</span>
            <span class="cart-upsell__price">${fmtEuro(p.price)}</span>
          </a>
        </div>`;
      }).join('');
      wrap.hidden = false;
    } catch (e) { /* noop */ }
  }
  loadCartUpsell();

  /* ---------- Product lightbox ---------- */
  const lightbox = document.getElementById('Lightbox');
  if (lightbox) {
    const lbTrack = qs('[data-lightbox-track]', lightbox);
    const lbPrev = qs('[data-lightbox-prev]', lightbox);
    const lbNext = qs('[data-lightbox-next]', lightbox);
    const galTrack = qs('[data-gallery-track]');
    const resetZoom = () => qsa('.lightbox__slide img.is-zoomed', lightbox).forEach((im) => im.classList.remove('is-zoomed'));
    const lbCount = lbTrack.children.length;
    const lbIndex = () => Math.round(lbTrack.scrollLeft / lbTrack.clientWidth);
    const syncNav = () => {
      if (!lbPrev) return;
      const i = lbIndex();
      lbPrev.disabled = i <= 0;
      lbNext.disabled = i >= lbCount - 1;
    };
    const openLightbox = (index) => {
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      loadSlide(lightbox);
      lockScroll();
      document.documentElement.classList.add('no-scroll');
      const slide = lbTrack.children[index];
      if (slide) lbTrack.scrollTo({ left: slide.offsetLeft });
      syncNav();
      qs('.lightbox__close', lightbox).focus();
    };
    const closeLightbox = () => {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      resetZoom();
      document.documentElement.classList.remove('no-scroll');
      unlockScroll();
    };
    const lbStep = (dir) => {
      resetZoom();
      lbTrack.scrollBy({ left: dir * lbTrack.clientWidth, behavior: 'smooth' });
    };
    if (lbPrev) lbPrev.addEventListener('click', () => lbStep(-1));
    if (lbNext) lbNext.addEventListener('click', () => lbStep(1));
    lbTrack.addEventListener('scroll', () => requestAnimationFrame(syncNav), { passive: true });
    qsa('.lightbox__slide img', lightbox).forEach((img) => {
      img.addEventListener('click', (e) => {
        const zoomed = img.classList.toggle('is-zoomed');
        if (zoomed) {
          const slide = img.closest('.lightbox__slide');
          const rx = e.offsetX / img.offsetWidth;
          const ry = e.offsetY / img.offsetHeight;
          requestAnimationFrame(() => {
            slide.scrollLeft = img.offsetWidth * rx - slide.clientWidth / 2;
            slide.scrollTop = img.offsetHeight * ry - slide.clientHeight / 2;
          });
        }
      });
    });
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-lightbox-open]')) {
        const i = galTrack ? Math.round(galTrack.scrollLeft / galTrack.clientWidth) : 0;
        openLightbox(i);
        return;
      }
      if (e.target.closest('[data-lightbox-close]')) closeLightbox();
    });
    qsa('.pdp-gallery__slide img').forEach((img, i) => {
      img.addEventListener('click', () => openLightbox(i));
    });
    document.addEventListener('keydown', (e) => {
      if (!lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') lbStep(-1);
      if (e.key === 'ArrowRight') lbStep(1);
    });
  }

  /* ---------- Announcement rotation ---------- */
  const annBar = qs('[data-announcement]');
  if (annBar) {
    const msgs = qsa('.announcement__msg', annBar);
    if (msgs.length > 1) {
      let annIdx = 0;
      const secs = Number(annBar.dataset.rotate) || 5;
      const setMsg = (i) => {
        annIdx = (i + msgs.length) % msgs.length;
        msgs.forEach((m, j) => m.classList.toggle('is-active', j === annIdx));
      };
      let annTimer = setInterval(() => setMsg(annIdx + 1), secs * 1000);
      const annStep = (dir) => {
        clearInterval(annTimer);
        setMsg(annIdx + dir);
        annTimer = setInterval(() => setMsg(annIdx + 1), secs * 1000);
      };
      const prevBtn = qs('[data-ann-prev]', annBar);
      const nextBtn = qs('[data-ann-next]', annBar);
      if (prevBtn) prevBtn.addEventListener('click', () => annStep(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => annStep(1));
    }
  }

  /* ---------- Wishlist (local) ---------- */
  const WISH_KEY = 'coolchap_wishlist';
  const readWish = () => { try { return JSON.parse(localStorage.getItem(WISH_KEY)) || []; } catch (e) { return []; } };
  qsa('[data-wish]').forEach((btnEl) => {
    const handle = btnEl.dataset.wish;
    const sync = () => btnEl.classList.toggle('is-active', readWish().includes(handle));
    btnEl.addEventListener('click', () => {
      let list = readWish();
      list = list.includes(handle) ? list.filter((h) => h !== handle) : list.concat(handle);
      try { localStorage.setItem(WISH_KEY, JSON.stringify(list)); } catch (e) { /* noop */ }
      sync();
    });
    sync();
  });

  /* ---------- Product highlights: deferred images + mobile carousel dots ---------- */
  qsa('[data-highlights]').forEach((wrap) => {
    const track = qs('[data-highlights-track]', wrap);
    const dots = qsa('.highlights__dots i', wrap);
    const items = qsa('[data-highlight-item]', wrap);
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => { if (en.isIntersecting) { loadSlide(en.target); io.unobserve(en.target); } });
      }, { rootMargin: '200px 120px' });
      items.forEach((it) => io.observe(it));
    } else {
      items.forEach(loadSlide);
    }
    if (!track || !dots.length) return;
    let raf = 0;
    track.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const first = track.firstElementChild;
        const step = first ? first.getBoundingClientRect().width + 10 : track.clientWidth;
        const i = Math.round(track.scrollLeft / step);
        dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
      });
    }, { passive: true });
  });

  /* ---------- Search drawer: live results via the Predictive Search API ---------- */
  const searchForm = qs('[data-search-form]');
  if (searchForm) {
    const input = qs('[data-search-input]', searchForm);
    const clearBtn = qs('[data-search-clear]', searchForm);
    const idle = qs('[data-search-idle]');
    const results = qs('[data-search-results]');
    const collBlock = qs('[data-search-collections-block]', results);
    const collList = qs('[data-search-collections]', results);
    const prodBlock = qs('[data-search-products-block]', results);
    const prodList = qs('[data-search-products]', results);
    const allLink = qs('[data-search-all]', results);
    const empty = qs('[data-search-empty]', results);
    const esc = (str) => String(str).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
    const highlight = (title, q) => {
      const safe = esc(title);
      const needle = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return needle ? safe.replace(new RegExp('(' + esc(needle) + ')', 'ig'), '<mark>$1</mark>') : safe;
    };
    const priceHtml = (p) => {
      const cents = Math.round(parseFloat(p.price) * 100) || 0;
      const compare = Math.round(parseFloat(p.compare_at_price_max || p.compare_at_price || 0) * 100) || 0;
      return (compare > cents ? `<s>${moneyFmt(compare)}</s>` : '') + moneyFmt(cents);
    };
    let timer = 0;
    let ctrl = null;
    const showIdle = () => { results.hidden = true; idle.hidden = false; };
    const render = (data, q) => {
      const products = (data.resources && data.resources.results.products) || [];
      const collections = (data.resources && data.resources.results.collections) || [];
      collList.innerHTML = collections.map((c) => `<li><a class="search-term" href="${esc(c.url)}"><span>${highlight(c.title, q)}</span>${'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M3.5 12h17M14 5.5l6.5 6.5L14 18.5"/></svg>'}</a></li>`).join('');
      collBlock.hidden = !collections.length;
      prodList.innerHTML = products.map((p) => {
        const img = p.featured_image && p.featured_image.url ? p.featured_image.url.replace(/(\.[a-z]+)(\?|$)/i, '_112x112$1$2') : '';
        const sub = [p.vendor, p.product_type].filter(Boolean).join(' · ');
        return `<li><a class="search-result" href="${esc(p.url)}">
          <span class="search-result__thumb">${img ? `<img src="${esc(img)}" alt="" width="56" height="56" loading="lazy">` : ''}</span>
          <span class="search-result__meta"><span class="search-result__title">${highlight(p.title, q)}</span>${sub ? `<span class="search-result__sub">${esc(sub)}</span>` : ''}</span>
          <span class="search-result__price">${priceHtml(p)}</span>
        </a></li>`;
      }).join('');
      prodBlock.hidden = !products.length;
      allLink.href = `${searchForm.getAttribute('action')}?q=${encodeURIComponent(q)}`;
      allLink.textContent = searchForm.dataset.viewAll.replace('__terms__', q);
      const none = !products.length && !collections.length;
      empty.hidden = !none;
      if (none) empty.textContent = searchForm.dataset.noResults.replace('__terms__', q);
      idle.hidden = true;
      results.hidden = false;
      results.classList.remove('is-loading');
    };
    const suggest = (q) => {
      if (ctrl) ctrl.abort();
      ctrl = new AbortController();
      results.classList.add('is-loading');
      const url = `${window.routes.root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,collection&resources[limit]=6&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,vendor,variants.title`;
      fetch(url.replace('//search', '/search'), { signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((data) => render(data, q))
        .catch((err) => { if (err && err.name !== 'AbortError') results.classList.remove('is-loading'); });
    };
    input.addEventListener('input', () => {
      const q = input.value.trim();
      clearBtn.hidden = q === '';
      clearTimeout(timer);
      if (q.length < 2) { if (ctrl) ctrl.abort(); showIdle(); return; }
      timer = setTimeout(() => suggest(q), 220);
    });
    clearBtn.addEventListener('click', () => {
      input.value = '';
      clearBtn.hidden = true;
      showIdle();
      input.focus();
    });
  }

  /* ---------- Filter sheet: drop empty params (price min/max) so URLs stay clean ---------- */
  qsa('.filter-sheet__form').forEach((form) => {
    const sortLabel = qs('[data-sort-current]', form);
    form.addEventListener('change', (e) => {
      if (sortLabel && e.target.matches('[data-sort-option]')) sortLabel.textContent = e.target.nextElementSibling.textContent;
    });
    form.addEventListener('submit', () => {
      qsa('input[type="number"], input[type="text"]', form).forEach((i) => { if (i.value.trim() === '') i.disabled = true; });
    });
  });

  /* ---------- Localization form ---------- */
  qsa('[data-localization-form] select, select[data-loc-auto]').forEach((sel) => {
    sel.addEventListener('change', () => sel.closest('form').submit());
  });

  /* ---------- Footer accordions: forced open on desktop ---------- */
  const desktopMq = window.matchMedia('(min-width: 990px)');
  const syncFooterAcc = () => {
    qsa('.site-footer__grid .footer-acc').forEach((d) => { d.open = desktopMq.matches; });
  };
  syncFooterAcc();
  desktopMq.addEventListener('change', syncFooterAcc);
})();
